import React, { useEffect, useRef } from 'react';
import { XMarkIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import api from '../../api/client';
import { useFamilyStore } from '../../store/familyStore';
import { useNavigate } from 'react-router-dom';
import { useMyActivities, getActivityIcon, getActivityColor, Activity } from '../../hooks/useActivities';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  open: boolean;
  onClose: () => void;
}

const NotificationsPanel: React.FC<Props> = ({ open, onClose }) => {
  const { currentFamily } = useFamilyStore();
  const navigate = useNavigate();
  const wsRef = useRef<WebSocket | null>(null);
  const queryClient = useQueryClient();
  
  // Activity log data
  const { data: activitiesData, isLoading: activitiesLoading, refetch: refetchActivities } = useMyActivities(20);

  // Websocket for realtime activity log updates
  useEffect(() => {
    if (!open) return;
    
    const token = localStorage.getItem('access_token');
    if (!currentFamily?.id || !token) return;

    const base = (import.meta.env.VITE_API_URL || '').replace(/^http/, 'ws');
    if (!base) return;

    const socket = new WebSocket(`${base}/ws/families/${currentFamily.id}?token=${token}`);
    wsRef.current = socket;

    socket.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === 'activity_created') {
          refetchActivities();
        }
      } catch {
        // ignore malformed events
      }
    };

    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    socket.onclose = () => {
      wsRef.current = null;
    };

    return () => {
      if (socket.readyState === WebSocket.OPEN) {
        socket.close();
      }
      wsRef.current = null;
    };
  }, [open, currentFamily?.id, refetchActivities]);

  // Dispatch activity count on load
  useEffect(() => {
    if (open && activitiesData?.activities) {
      const count = activitiesData.activities.length;
      window.dispatchEvent(new CustomEvent('notificationsUpdated', { detail: { unreadCount: count } }));
    }
  }, [open, activitiesData?.activities]);

  const handleClearAll = async () => {
    if (!activitiesData?.activities?.length) return;
    try {
      await api.delete('/activities/clear');
      // Refetch to get empty list
      queryClient.invalidateQueries({ queryKey: ['my-activities'] });
      toast.success('Activity cleared');
      // Dispatch event for unread count update
      window.dispatchEvent(new CustomEvent('notificationsUpdated', { detail: { unreadCount: 0 } }));
    } catch {
      toast.error('Failed to clear activity');
    }
  };

  const renderActivityItem = (activity: Activity) => (
    <button
      key={activity.id}
      onClick={() => {
        navigate(activity.navigation_url);
        onClose();
      }}
      className="w-full flex items-center gap-3 p-3 text-left hover:bg-white/5 rounded-lg transition-colors"
    >
      {/* Activity Icon */}
      <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${getActivityColor(activity.activity_type)} flex items-center justify-center text-sm shrink-0`}>
        {getActivityIcon(activity.activity_type)}
      </div>
      
      {/* Activity Content */}
      <div className="flex-1 min-w-0 overflow-hidden">
        <p 
          className="text-white font-medium text-sm leading-snug truncate" 
          title={activity.title}
        >
          {activity.title}
        </p>
        <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
          <span className="text-xs text-surface-400 truncate min-w-0 flex-shrink">
            {activity.user_name}
          </span>
          <span className="text-surface-600 shrink-0 text-xs">•</span>
          <span className="text-xs text-surface-500 shrink-0 whitespace-nowrap">
            {formatDistanceToNow(new Date(activity.created_at), { addSuffix: true })}
          </span>
        </div>
      </div>
      
      {/* Arrow */}
      <ArrowRightIcon className="w-4 h-4 text-surface-500 shrink-0" />
    </button>
  );

  return (
    <div
      className={`fixed top-0 right-0 w-full sm:w-96 h-full bg-surface-900 border-l border-surface-700 shadow-2xl transform transition-transform duration-200 z-50 ${
        open ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      <div className="flex items-center justify-between p-4 border-b border-surface-700">
        <div className="flex items-center gap-2">
          <button className="btn-icon" onClick={onClose}>
            <ArrowRightIcon className="w-5 h-5 rotate-180" />
          </button>
          <h3 className="text-lg font-semibold text-white">Activity</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary btn-sm"
            disabled={!activitiesData?.activities?.length || activitiesLoading}
            onClick={handleClearAll}
          >
            Clear all
          </button>
          <button className="btn-icon" onClick={onClose}>
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
      
      <div className="p-4 space-y-3 overflow-y-auto h-[calc(100%-80px)]">
        {activitiesLoading && (
          <div className="flex items-center justify-center py-6">
            <div className="w-6 h-6 border-2 border-primary-500/30 border-t-primary-500 rounded-full animate-spin" />
          </div>
        )}
        {!activitiesLoading && !activitiesData?.activities?.length && (
          <div className="text-center py-6 text-surface-400">
            <p className="text-sm">No recent activity</p>
            <p className="text-xs mt-1">Activity from your family will appear here</p>
          </div>
        )}
        {!activitiesLoading && activitiesData?.activities && activitiesData.activities.length > 0 && (
          <div className="space-y-1">
            {activitiesData.activities.map((activity) => renderActivityItem(activity))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;

